import { IconButton, Stack, Text } from '@chakra-ui/react'
import { faEye, faEyeSlash, faSend } from '@fortawesome/pro-regular-svg-icons'
import { useRouter } from 'next/router'
import pMap from 'p-map'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { noOp } from '@/utils/helpers'
import logger from '@/utils/logger'
import { usePersistedState } from '@/utils/usePersistedState'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import useUserPresenceHeartbeat from '@/client/hooks/useUserPresenceHeartbeat'
import RPC from '@/client/rpc'
import { EMPTY_RICH_TEXT_VALUE } from '@/client/services/richText/constants'
import getMentionedUserIds from '@/client/services/richText/getMentionedUserIds'
import isEmpty from '@/client/services/richText/isEmpty'
import type { SerializedRichText } from '@/client/services/richText/types'
import uploadFile from '@/client/services/userUpload/uploadFile'

import Alert from '@/components/UI/Alert'
import Icon from '@/components/UI/Icon/Icon'
import RichTextEditorSimple from '@/components/UI/RichTextEditor/RichTextEditorSimple/RichTextEditorSimple'
import type { RichTextEditorSimpleRef } from '@/components/UI/RichTextEditor/RichTextEditorSimple/types'
import type { MentionsProps } from '@/components/UI/RichTextEditor/types'
import Tippy from '@/components/UI/Tippy'
import type { FileUpload } from '@/components/Worlds/FileUpload/types'
import IconButtonAction from '@/components/Worlds/UI/Button/IconButtonAction'

export default function NewComment({
  postId,
  mentions,
  onSubmit = noOp,
  onAttachmentUploadComplete = noOp,
  onSubmitFailed = noOp,
}: {
  postId: number
  mentions?: MentionsProps
  onSubmit?: (p: {
    body: SerializedRichText
    isCollapsible: boolean
    clientId: string
    attachments: FileUpload[]
  }) => void
  onAttachmentUploadComplete?: (params: { clientId: string; src: string }) => void
  onSubmitFailed?: (clientId: string) => void
}): JSX.Element {
  const router = useRouter()
  const heartbeat = useUserPresenceHeartbeat({ postId }, 2500)

  const editorRef = useRef<RichTextEditorSimpleRef>(null)

  const [body, setBody] = useState<SerializedRichText>(EMPTY_RICH_TEXT_VALUE)
  const [blockedError, setBlockedError] = useState<string | null>(null)
  const [isCollapsible, setIsCollapsible] = useState(false)

  const isBodyEmpty = useMemo(() => isEmpty(body), [body])

  const { previousStateClear, saveState, previousStateAvailable, previousStateRestore } =
    usePersistedState<{
      body: SerializedRichText
    }>(`post-comment:${postId}`, { body: EMPTY_RICH_TEXT_VALUE })

  useEffect(() => {
    if (previousStateAvailable) {
      const { body: savedBody } = previousStateRestore()
      editorRef.current?.setValue(savedBody)
      setBody(savedBody)
    }
  }, [previousStateAvailable, previousStateRestore, postId])

  const [handleCreateComment, isCreatingComment] = useAPIWrapper(
    async () => {
      if (isBodyEmpty) {
        return
      }

      const clientId = uuidv4()

      setBlockedError(null)

      const uploads = editorRef.current?.getUploads() ?? []
      editorRef.current?.clear()

      const optimisticAttachments = uploads.map(item => ({
        loading: true,
        src: URL.createObjectURL(item),
        file: item,
      }))

      onSubmit({
        body,
        attachments: optimisticAttachments,
        isCollapsible,
        clientId,
      })

      setBody(EMPTY_RICH_TEXT_VALUE)
      setIsCollapsible(false)

      const attachmentUrls = await pMap(
        optimisticAttachments,
        async ({ file, src }) => {
          const { downloadUrl } = await uploadFile({
            file,
            fileType: file.type,
            target: { type: 'attachment' },
          })
          onAttachmentUploadComplete({ clientId, src })
          return downloadUrl
        },
        { concurrency: 3 }
      )

      try {
        const createPostCommentResult = await RPC.createPostComment({
          postId,
          body,
          attachmentUrls,
          mentionedUserIds: getMentionedUserIds(body),
          clientId,
          isCollapsible,
        })

        previousStateClear()

        if (createPostCommentResult.status === 'blocked') {
          onSubmitFailed(clientId)
          setBlockedError(createPostCommentResult.message)
          return
        }

        if (createPostCommentResult.status === 'suspended') {
          router.reload()
        }
      } catch (error) {
        logger.warning('Error posting comment', { error })
        onSubmitFailed(clientId)
      }
    },
    [
      isBodyEmpty,
      onSubmit,
      body,
      postId,
      isCollapsible,
      onAttachmentUploadComplete,
      onSubmitFailed,
      router,
      previousStateClear,
    ],
    'Create Post Comment'
  )

  const handleChange = useCallback(
    (value: SerializedRichText) => {
      heartbeat()
      setBody(value)
      saveState('body', value)
    },
    [heartbeat, saveState]
  )

  return (
    <Stack>
      {blockedError == null ? null : (
        <Alert variant="error">
          <Text>{blockedError}</Text>
        </Alert>
      )}
      {isCollapsible ? (
        <Text color="fg.muted" variant="supporting">
          This comment will be collapsed by default.
        </Text>
      ) : null}
      <RichTextEditorSimple
        ref={editorRef}
        name="CreatePostComment"
        placeholder="Reply..."
        initialValue={EMPTY_RICH_TEXT_VALUE}
        disableAutofocus
        onEnter={handleCreateComment}
        onChange={handleChange}
        disableNewLineOnEnter
        leftFooterContent={() => (
          <Tippy content={isCollapsible ? 'Uncollapse Comment' : 'Collapse Comment'}>
            <IconButton
              onClick={() => setIsCollapsible(!isCollapsible)}
              icon={
                <Icon
                  faIcon={isCollapsible ? faEyeSlash : faEye}
                  size="md"
                  color={isCollapsible ? 'primary.fg' : 'fg.muted'}
                />
              }
              aria-label="Toggle Collapse"
              variant="icon"
            />
          </Tippy>
        )}
        mentions={mentions}
        rightFooterContent={() => (
          <IconButtonAction
            label="Send message"
            faIcon={faSend}
            onClick={handleCreateComment}
            isDisabled={isBodyEmpty || isCreatingComment}
          />
        )}
      />
    </Stack>
  )
}
